import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import usePageTitle from '../../hooks/UsePageTitle';

const ProjectFinancing = () => {
  usePageTitle('Project Financing');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">Project Financing</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/financing1.png' 
            alt="Project Financing" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              In the world of business expansion, infrastructure development, and strategic scaling, 
              access to timely and structured financing is key. Whether you are setting up a new venture, 
              expanding operations, acquiring assets, or entering new markets, the right financial backing 
              makes all the difference.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, we specialize in providing comprehensive Project Financing Advisory services 
              tailored to meet the specific funding needs of businesses across sectors.
            </p>
            <p className="text-lg line-height-3 mt-3">
              We work closely with promoters, CFOs, and management teams to structure funding that aligns 
              with business goals while ensuring regulatory compliance, optimal cost of capital, and long-term 
              sustainability.
            </p>
          </Card>
        </div>

        {/* Our Approach Section */}
        <div className="col-12 mt-5">
          <h2>Our Approach to Project Financing</h2>
          <Divider />
          <div className="flex flex-column md:flex-row gap-5">
            <img 
              src='/images/financing2.png' 
              alt="Financing Approach" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '300px', objectFit: 'cover' }}
            />
            <div className="flex-1">
              <p className="text-xl line-height-3">
                Project financing is not just about raising funds — it's about designing a financing strategy 
                that balances risk, secures investor confidence, and fuels execution without burdening cash flows. 
                Our services cover the entire lifecycle of project funding, from concept and viability assessment 
                to deal closure and fund utilization monitoring.
              </p>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="col-12 mt-5">
          <h2>Our Comprehensive Services Include:</h2>
          <Divider />
          
          <div className="grid">
            {/* Service 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Project Feasibility & Viability Analysis" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Preparation of Detailed Project Reports (DPRs) covering technical, commercial, legal, and financial feasibility.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Market research, demand-supply analysis, competition mapping, and SWOT evaluation.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Revenue modeling, cost structure estimation, and break-even timelines.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Risk analysis including industry-specific risks, regulatory frameworks, and mitigation strategies.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. Financial Modelling & Projections" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Development of dynamic financial models including profitability, cash flow, IRR, and DSCR analysis.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Sensitivity analysis for stress testing assumptions and scenarios.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Forecasting working capital requirements, repayment schedules, and fund flow statements.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. Capital Structuring & Strategy" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identifying the right funding mix: equity, debt, mezzanine capital, or hybrid instruments.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Advising on promoter contribution, equity dilution, collateral optimization, and debt servicing strategies.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Structuring Special Purpose Vehicles (SPVs) for infrastructure, real estate, or PPP projects where applicable.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Debt Syndication & Bank Financing" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification and approach to appropriate banks, NBFCs, and financial institutions.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Preparation of funding proposals, CMA data, and banker-specific documentation.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Negotiation of terms including interest rates, tenure, moratoriums, and security arrangements.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Coordination for term loans, working capital limits, ECBs, or refinance facilities.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Equity Funding & Private Investment" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Pitch deck preparation and investment memorandum creation for PE/VC firms.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Investor identification and matchmaking based on sector, ticket size, and investment philosophy.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assistance with due diligence, valuation negotiations, and shareholder agreement structuring.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 6 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="6. Government Grants & Subsidy Advisory" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Identification of central/state government incentives, subsidies, or grants relevant to the project.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Assistance in preparing documentation, applications, and follow-ups for fund disbursements.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Tracking of utilization and compliance with grant conditions.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 7 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="7. Transaction Support & Compliance" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Legal and regulatory advisory including FEMA, Companies Act, SEBI, and RBI guidelines.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Drafting of loan agreements, promoter undertakings, term sheets, and security creation documents.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Coordination with legal counsels, valuation experts, and credit rating agencies.</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Service 8 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="8. Post-Funding Monitoring & Reporting" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Setup of MIS (Management Information Systems) to track fund utilization, milestone achievements, and cost overruns.</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Preparation of periodic reports for lenders, investors, and stakeholders.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Advisory on restructuring or refinancing in case of delays or project pivots.</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Closing Image */}
        <div className="col-12 mt-5">
          <img 
            src='/images/financing3.png' 
            alt="Project Financing Solutions" 
            className="w-full border-round shadow-2" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectFinancing;